/**
 * Add/edit BottomSheet form for Plaatsen (spec §8.5).
 *
 * Fields: object, main category, subzone, exact location, notes, photo
 * (add/replace/remove), save, cancel. The footer is a visible SaveBar.
 *
 * P0 truthfulness rules implemented here:
 * - on a failed cloud save the form NEVER resets: values, local preview and
 *   the picked File stay intact, an explicit retry is shown, and no cloud-save
 *   copy is ever displayed;
 * - while editing, a device-only draft is persisted to localStorage so the
 *   data survives a reload ('rust-places-drafts', labelled 'Alleen op dit
 *   apparaat' until the cloud confirms).
 */
import { useEffect, useRef, useState, type ChangeEvent } from 'react'
import { Banner, BottomSheet, Button, SaveBar, Select, Spinner, TextArea, TextField } from '@/components/ui'
import { CameraIcon, ImageIcon, TrashIcon } from '@/components/icons'
import type { AppError, Place, SyncSaveState } from '@/types'
import { useImageUpload } from '@/features/media'
import { PLACE_CATEGORIES } from './categories'
import type { PlaceDraft } from './drafts'
import { usePlaceThumbnail } from './usePlaceThumbnail'
import type { PlaceFormValues, SavePlaceInput, SavePlaceResult } from './usePlaces'

export type PlaceFormTarget =
  | { kind: 'new'; prefillCategory: string }
  | { kind: 'edit'; place: Place }
  | { kind: 'draft'; draft: PlaceDraft }

type PlaceFormSheetProps = {
  open: boolean
  target: PlaceFormTarget
  onSave: (input: SavePlaceInput) => Promise<SavePlaceResult>
  /** Called after a confirmed cloud save; the parent closes the sheet. */
  onSaved: () => void
  /** Explicit discard: closes the sheet and removes the device-only draft. */
  onCancel: () => void
  onPersistDraft: (draft: PlaceDraft) => void
}

function initialValues(target: PlaceFormTarget): PlaceFormValues {
  if (target.kind === 'new') {
    return {
      objectLabel: '',
      category: target.prefillCategory,
      subzone: '',
      exactLocation: '',
      notes: '',
    }
  }
  if (target.kind === 'edit') {
    return {
      objectLabel: target.place.objectLabel,
      category: target.place.category,
      subzone: target.place.subzone ?? '',
      exactLocation: target.place.exactLocation ?? '',
      notes: target.place.notes ?? '',
    }
  }
  return {
    objectLabel: target.draft.objectLabel,
    category: target.draft.category,
    subzone: target.draft.subzone,
    exactLocation: target.draft.exactLocation,
    notes: target.draft.notes,
  }
}

function ExistingPlacePhoto({ place }: { place: Place }) {
  const { url, loading } = usePlaceThumbnail(place)
  if (loading) {
    return (
      <div className="places-photo-frame places-photo-feedback">
        <Spinner size={20} />
        <span>Foto laden…</span>
      </div>
    )
  }
  if (!url) {
    return (
      <div className="places-photo-frame places-photo-feedback" role="img" aria-label="Foto niet beschikbaar">
        <ImageIcon size={24} />
        <span>Foto niet beschikbaar</span>
      </div>
    )
  }
  return <img className="places-photo-frame places-photo-img" src={url} alt={`Huidige foto van ${place.objectLabel}`} />
}

export function PlaceFormSheet({ open, target, onSave, onSaved, onCancel, onPersistDraft }: PlaceFormSheetProps) {
  const editingPlace = target.kind === 'edit' ? target.place : null
  const placeId = target.kind === 'edit' ? target.place.id : target.kind === 'draft' ? target.draft.placeId : null

  const [values, setValues] = useState<PlaceFormValues>(() => initialValues(target))
  const [objectLabelError, setObjectLabelError] = useState<string | null>(null)
  const [removeImageRequested, setRemoveImageRequested] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveState, setSaveState] = useState<SyncSaveState | null>(null)
  const [saveError, setSaveError] = useState<AppError | null>(null)
  const dirtyRef = useRef(false)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const upload = useImageUpload()
  const { restoreFromDraftDataUrl } = upload

  // Restore a persisted draft photo once after a reload.
  const restoredRef = useRef(false)
  useEffect(() => {
    if (restoredRef.current) return
    restoredRef.current = true
    if (target.kind === 'draft' && target.draft.imageDataUrl) {
      void restoreFromDraftDataUrl(target.draft.imageDataUrl, 'foto.jpg')
    }
  }, [target, restoreFromDraftDataUrl])

  // Debounced device-only draft persistence while editing (survives reloads).
  useEffect(() => {
    if (!open || !dirtyRef.current) return
    const handle = setTimeout(() => {
      onPersistDraft({
        placeId,
        objectLabel: values.objectLabel.trim(),
        category: values.category,
        subzone: values.subzone.trim(),
        exactLocation: values.exactLocation.trim(),
        notes: values.notes.trim(),
        imageDataUrl: upload.draftDataUrl,
        state:
          saveState === 'sync_failed' || saveState === 'sync_queued' ? saveState : 'local_saved',
        updatedAt: new Date().toISOString(),
      })
    }, 400)
    return () => clearTimeout(handle)
  }, [open, values, upload.draftDataUrl, saveState, placeId, onPersistDraft])

  const updateValue = (patch: Partial<PlaceFormValues>) => {
    dirtyRef.current = true
    setValues((previous) => ({ ...previous, ...patch }))
  }

  const openFilePicker = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    dirtyRef.current = true
    setRemoveImageRequested(false)
    void upload.selectFile(file)
  }

  const handleRemovePhoto = () => {
    dirtyRef.current = true
    upload.clear()
    if (editingPlace?.imagePath) setRemoveImageRequested(true)
  }

  const hasExistingPhoto = Boolean(editingPlace?.imagePath) && !removeImageRequested
  const hasAnyPhoto = Boolean(upload.previewUrl) || hasExistingPhoto

  const handleSave = async () => {
    if (saving) return
    if (!values.objectLabel.trim()) {
      setObjectLabelError('Vul een voorwerp in.')
      return
    }
    setObjectLabelError(null)
    setSaveError(null)
    setSaving(true)
    setSaveState('syncing')
    const result = await onSave({
      placeId,
      values,
      imageFile: upload.file,
      imageDraftDataUrl: upload.draftDataUrl,
      removeImage: removeImageRequested && !upload.file,
    })
    setSaving(false)
    if (result.ok) {
      setSaveState('cloud_saved')
      onSaved()
      return
    }
    // Failed cloud save: keep everything intact and offer an explicit retry.
    setSaveState(result.state)
    setSaveError(result.error)
  }

  const title =
    target.kind === 'new' ? 'Nieuwe plaats' : target.kind === 'edit' ? 'Plaats bewerken' : 'Concept verder bewerken'

  const retryLabel = saveState === 'sync_failed' || saveState === 'sync_queued' ? 'Opnieuw proberen' : 'Opslaan'

  return (
    <BottomSheet
      open={open}
      onClose={onCancel}
      title={title}
      footer={
        <SaveBar
          visible
          saving={saving}
          saveLabel={retryLabel}
          onSave={() => {
            void handleSave()
          }}
          onCancel={onCancel}
          state={saving ? 'syncing' : saveState}
        />
      }
    >
      <div className="places-form">
        {saveError ? (
          <Banner
            tone="error"
            action={
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  void handleSave()
                }}
                disabled={saving}
              >
                Opnieuw proberen
              </Button>
            }
          >
            {saveError.userMessage} Je gegevens zijn bewaard op dit apparaat.
          </Banner>
        ) : null}

        <TextField
          label="Voorwerp"
          value={values.objectLabel}
          onChange={(objectLabel) => updateValue({ objectLabel })}
          error={objectLabelError ?? undefined}
          required
          placeholder="Bijv. sleutels, oplader, bril"
        />

        <Select
          label="Hoofdcategorie"
          value={values.category}
          onChange={(category) => updateValue({ category })}
          options={PLACE_CATEGORIES.map((category) => ({ value: category, label: category }))}
        />

        <TextField
          label="Subzone"
          value={values.subzone}
          onChange={(subzone) => updateValue({ subzone })}
          placeholder="Bijv. nachtkastje, lade, kapstok"
          hint="Optioneel: een kleinere plek binnen de categorie."
        />

        <TextField
          label="Exacte locatie"
          value={values.exactLocation}
          onChange={(exactLocation) => updateValue({ exactLocation })}
          placeholder="Bijv. bovenste lade links"
        />

        <TextArea
          label="Notities"
          value={values.notes}
          onChange={(notes) => updateValue({ notes })}
          rows={3}
          placeholder="Extra details om te onthouden"
        />

        <div className="places-photo">
          <span className="places-photo-label">Foto</span>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="places-file-input"
            aria-label="Kies of maak een foto"
            onChange={handleFileChange}
          />

          {upload.status === 'validating' ? (
            <div className="places-photo-frame places-photo-feedback" aria-live="polite">
              <Spinner size={20} />
              <span>Foto controleren…</span>
            </div>
          ) : upload.previewUrl ? (
            <img className="places-photo-frame places-photo-img" src={upload.previewUrl} alt="Voorbeeld van de gekozen foto" />
          ) : hasExistingPhoto && editingPlace ? (
            <ExistingPlacePhoto place={editingPlace} />
          ) : null}

          {upload.error ? <Banner tone="error">{upload.error.userMessage}</Banner> : null}

          <div className="places-photo-actions">
            <Button variant="secondary" size="sm" onClick={openFilePicker} disabled={saving || upload.status === 'validating'}>
              <CameraIcon size={18} />
              {hasAnyPhoto ? 'Foto vervangen' : 'Foto toevoegen'}
            </Button>
            {hasAnyPhoto ? (
              <Button variant="ghost" size="sm" onClick={handleRemovePhoto} disabled={saving}>
                <TrashIcon size={18} />
                Foto verwijderen
              </Button>
            ) : null}
          </div>
        </div>
      </div>
    </BottomSheet>
  )
}
